import { useEffect, useRef } from 'react';

// 粒子密度与上限：随视口面积自适应，防止大屏过载
const PARTICLE_DENSITY = 1 / 15000;
const MAX_PARTICLES = 110;
const LINK_DISTANCE = 130;
// 鼠标交互：吸引半径 / 径向拉力 / 切向涡流力（粒子绕光标旋转）
const POINTER_RADIUS = 210;
const POINTER_PULL = 0.05;
const POINTER_SWIRL = 0.042;
const MAX_SPEED = 1.8;
// 松弛系数：鼠标离开后粒子回归基准漂移速度的快慢
const RELAX = 0.012;
// 拖尾：每帧按此透明度淡出上一帧，值越小彗尾越长
const TRAIL_FADE = 0.18;
const MAX_SPARKS = 140;

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  // 基准漂移速度：鼠标离开后粒子缓慢回归这个速度
  bvx: number;
  bvy: number;
  r: number;
  alpha: number;
  hue: number;
  phase: number;
};

// 点击爆发的火花粒子（带生命周期）
type Spark = { x: number; y: number; vx: number; vy: number; life: number; hue: number };
// 点击冲击波涟漪
type Ripple = { x: number; y: number; r: number; alpha: number };

function spawnParticle(width: number, height: number): Particle {
  const speed = 0.12 + Math.random() * 0.24;
  const angle = Math.random() * Math.PI * 2;
  const bvx = Math.cos(angle) * speed;
  // 整体略微向上漂，贴合"数据上升"的观感
  const bvy = Math.sin(angle) * speed - 0.06;
  return {
    x: Math.random() * width,
    y: Math.random() * height,
    vx: bvx,
    vy: bvy,
    bvx,
    bvy,
    r: 0.9 + Math.random() * 1.7,
    // 叠加发光模式下透明度要低，慢速粒子会逐帧累积变亮
    alpha: 0.1 + Math.random() * 0.22,
    // 青→蓝→紫色相区间，霓虹感
    hue: 190 + Math.random() * 70,
    phase: Math.random() * Math.PI * 2,
  };
}

/**
 * 登录页粒子背景：霓虹拖尾 + 鼠标涡流环绕 + 点击爆发冲击波。
 * 尊重 prefers-reduced-motion；页签隐藏时暂停，避免无谓耗电。
 */
export function LoginParticles() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let particles: Particle[] = [];
    const sparks: Spark[] = [];
    const ripples: Ripple[] = [];
    let rafId = 0;
    let running = true;
    let width = 0;
    let height = 0;
    const pointer = { x: 0, y: 0, active: false };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.min(MAX_PARTICLES, Math.round(width * height * PARTICLE_DENSITY));
      while (particles.length < target) particles.push(spawnParticle(width, height));
      if (particles.length > target) particles = particles.slice(0, target);
    };

    const step = (now: number) => {
      if (!running) return;
      // 用 destination-out 淡出上一帧，形成透明拖尾；之后全部叠加发光绘制
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = `rgba(0, 0, 0, ${TRAIL_FADE})`;
      ctx.fillRect(0, 0, width, height);
      ctx.globalCompositeOperation = 'lighter';

      for (const p of particles) {
        if (pointer.active) {
          const dx = pointer.x - p.x;
          const dy = pointer.y - p.y;
          const dist = Math.hypot(dx, dy);
          // 40px 内圈不再吸引：粒子在光标周围环绕，而不是塌缩成一点
          if (dist < POINTER_RADIUS && dist > 40) {
            const force = (1 - dist / POINTER_RADIUS) * POINTER_PULL;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
            // 切向力：让粒子绕光标公转，形成涡流
            const swirl = (1 - dist / POINTER_RADIUS) * POINTER_SWIRL;
            p.vx += (-dy / dist) * swirl;
            p.vy += (dx / dist) * swirl;
          }
        }
        // 缓慢回归基准漂移速度 + 限速，鼠标离开后恢复原状且不会飞散
        p.vx += (p.bvx - p.vx) * RELAX;
        p.vy += (p.bvy - p.vy) * RELAX;
        const speed = Math.hypot(p.vx, p.vy);
        if (speed > MAX_SPEED) {
          p.vx = (p.vx / speed) * MAX_SPEED;
          p.vy = (p.vy / speed) * MAX_SPEED;
        }
        p.x += p.vx;
        p.y += p.vy;
        // 越界后从对侧回卷，保持粒子总量稳定
        if (p.x < -12) p.x = width + 12;
        else if (p.x > width + 12) p.x = -12;
        if (p.y < -12) p.y = height + 12;
        else if (p.y > height + 12) p.y = -12;
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        if (!a) continue;
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          if (!b) continue;
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          if (dist >= LINK_DISTANCE) continue;
          const fade = (1 - dist / LINK_DISTANCE) * 0.08;
          ctx.strokeStyle = `hsla(${(a.hue + b.hue) / 2}, 90%, 70%, ${fade.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      if (pointer.active) {
        // 光标光晕（叠加发光会逐帧累积，透明度必须很低）
        const glow = ctx.createRadialGradient(pointer.x, pointer.y, 0, pointer.x, pointer.y, 120);
        glow.addColorStop(0, 'rgba(80, 190, 255, 0.05)');
        glow.addColorStop(1, 'rgba(80, 190, 255, 0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(pointer.x, pointer.y, 120, 0, Math.PI * 2);
        ctx.fill();
        // 光标与附近粒子连线，强化"追随"的视觉反馈
        for (const p of particles) {
          const dist = Math.hypot(pointer.x - p.x, pointer.y - p.y);
          if (dist >= POINTER_RADIUS) continue;
          const fade = (1 - dist / POINTER_RADIUS) * 0.12;
          ctx.strokeStyle = `rgba(140, 222, 255, ${fade.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(pointer.x, pointer.y);
          ctx.lineTo(p.x, p.y);
          ctx.stroke();
        }
      }

      for (const p of particles) {
        // 闪烁：透明度随相位正弦起伏
        const twinkle = 0.7 + 0.3 * Math.sin(now / 900 + p.phase);
        ctx.fillStyle = `hsla(${p.hue}, 92%, 72%, ${(p.alpha * twinkle).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        if (!s) continue;
        s.x += s.vx;
        s.y += s.vy;
        s.vx *= 0.96;
        s.vy *= 0.96;
        s.life -= 0.016;
        if (s.life <= 0) {
          sparks.splice(i, 1);
          continue;
        }
        ctx.fillStyle = `hsla(${s.hue}, 95%, 72%, ${(s.life * 0.6).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.4 + s.life * 1.8, 0, Math.PI * 2);
        ctx.fill();
      }

      for (let i = ripples.length - 1; i >= 0; i--) {
        const w = ripples[i];
        if (!w) continue;
        w.r += 3.4;
        w.alpha *= 0.94;
        if (w.alpha < 0.02) {
          ripples.splice(i, 1);
          continue;
        }
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = `rgba(120, 215, 255, ${w.alpha.toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(w.x, w.y, w.r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.lineWidth = 1;
      }

      rafId = requestAnimationFrame(step);
    };

    // canvas 是 pointer-events: none 的装饰层，事件挂在 window 上换算坐标
    const handlePointerMove = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    };

    const handlePointerLeave = () => {
      pointer.active = false;
    };

    // 点击爆发：一圈火花 + 一道扩散冲击波
    const handlePointerDown = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      ripples.push({ x, y, r: 6, alpha: 0.5 });
      const count = Math.min(18, MAX_SPARKS - sparks.length);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
        const speed = 1.2 + Math.random() * 2.4;
        sparks.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 1,
          hue: 190 + Math.random() * 70,
        });
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(rafId);
      } else if (!running) {
        running = true;
        rafId = requestAnimationFrame(step);
      }
    };

    resize();
    rafId = requestAnimationFrame(step);
    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    document.documentElement.addEventListener('pointerleave', handlePointerLeave);
    window.addEventListener('blur', handlePointerLeave);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerdown', handlePointerDown);
      document.documentElement.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('blur', handlePointerLeave);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="crest-login-particles" tabIndex={-1} />;
}
